import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Stack, TextField, Typography } from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { Coordinate, DeskShapeFeature, MultiPolygonCoordinates } from '../models/editor-feature';
import { extentOf } from '../utils/arrangement';
import { isUsableScale, type Scale } from '../utils/measurement';
import { deskShapeRing, footprintOf, type DeskFootprint } from '../utils/desk-symbols';

interface Props {
  /** The outlines being sized. Several at once when several desks are selected. */
  readonly shapes: readonly DeskShapeFeature[];
  readonly scale: Scale;
  /** The redrawn outlines, keyed by editor id, applied together as one step. */
  readonly onRedraw: (changes: ReadonlyMap<string, MultiPolygonCoordinates>) => void;
}

/** Two sizes this close are the same size: a centimetre is below anything anybody would type. */
const SAME_SIZE_METRES = 0.01;

/**
 * How big a desk really is.
 *
 * Width and height in metres, as it is seen on the plan, and nothing else. The editor used to
 * offer five outline shapes as well — rounded, circle, L-shape — and none of them survived
 * contact with a floor plan: the outline is a footprint, the app fills it in one flat colour,
 * and at the size a desk occupies the corner radius of a rectangle is invisible. What is worth
 * getting right is the size, which is what this is.
 *
 * With several outlines, a typed figure goes to every one of them and only that figure: making
 * every desk 1.4 m wide leaves each one's height as it was.
 */
const DeskSizeField = ({ shapes, scale, onRedraw }: Props): React.JSX.Element | null => {
  const { t } = useTranslation();
  const usable = isUsableScale(scale);

  if (!shapes.length) {
    return null;
  }

  // Read back off the drawing rather than stored: a desk shape carries no size of its own — it
  // is a ring of points, which is all the app and the database ever see. Measured with the
  // shape turned upright, because a rotated outline's bounding box is bigger than the outline,
  // and redrawing at the box's size grew the desk a little every time.
  const footprints = shapes.map((shape) => footprintOf(shape.coordinates, shape.rotation, scale));

  const redraw = (change: (footprint: DeskFootprint) => DeskFootprint): void => {
    const changes = new Map<string, MultiPolygonCoordinates>();

    shapes.forEach((shape, index) => {
      const extent = extentOf(shape);
      const centre: Coordinate = [(extent.minX + extent.maxX) / 2, (extent.minY + extent.maxY) / 2];

      changes.set(shape.id, [[deskShapeRing(centre, change(footprints[index]), scale, shape.rotation)]]);
    });

    onRedraw(changes);
  };

  /** The one size every outline shares, or undefined when they differ. */
  const sharedOf = (read: (footprint: DeskFootprint) => number): number | undefined => {
    const first = read(footprints[0]);

    return footprints.every((footprint) => Math.abs(read(footprint) - first) < SAME_SIZE_METRES)
      ? first
      : undefined;
  };

  const metreField = (
    labelKey: string,
    read: (footprint: DeskFootprint) => number,
    write: (footprint: DeskFootprint, metres: number) => DeskFootprint,
  ): React.JSX.Element => {
    const shared = sharedOf(read);

    return (
      <TextField
        size="small"
        type="number"
        disabled={!usable}
        value={shared === undefined ? '' : Number(shared.toFixed(2))}
        placeholder={shared === undefined ? String(t('floorplan-editor-desk-size-mixed')) : undefined}
        onChange={(event): void => {
          const metres = Number(event.target.value);

          // Ignored rather than clamped: a half-typed "0." is on its way to a real number, and
          // snapping it to a minimum fights the person typing.
          if (event.target.value !== '' && Number.isFinite(metres) && metres > 0) {
            redraw((footprint) => write(footprint, metres));
          }
        }}
        label={String(t(labelKey))}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: 0.3, step: 0.1, 'aria-label': String(t(labelKey)) }}
      />
    );
  };

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {shapes.length > 1
          ? t('floorplan-editor-desk-size-many', { count: shapes.length })
          : t('floorplan-editor-desk-size')}
      </Typography>

      {!usable && (
        <Alert severity="info" sx={{ py: 0 }}>{t('floorplan-editor-desk-size-needs-scale')}</Alert>
      )}

      <Stack direction="row" spacing={1}>
        {metreField(
          'floorplan-editor-desk-width',
          (footprint) => footprint.widthMetres,
          (footprint, metres) => ({ ...footprint, widthMetres: metres }),
        )}
        {metreField(
          'floorplan-editor-desk-depth',
          (footprint) => footprint.depthMetres,
          (footprint, metres) => ({ ...footprint, depthMetres: metres }),
        )}
      </Stack>
    </Stack>
  );
};

export default DeskSizeField;
